import React, { useState, useEffect } from 'react';
import { db } from './firebase'; 
import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import emailjs from '@emailjs/browser'; 
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaClock, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import miLogo from './assets/mi-logo.png'; 

registerLocale('es', es);

// 🔥 JARVIS: Matriz de horarios agrupada en pares exactos (2 botones por bloque)
const BLOQUES_HORARIOS = [
    { id: "Bloque 1 (10:00 - 11:00)", opciones: ["10:00", "10:30"] },
    { id: "Bloque 2 (11:00 - 12:00)", opciones: ["11:00", "11:30"] },
    { id: "Bloque 3 (12:00 - 13:00)", opciones: ["12:00", "12:30"] },
    { id: "Bloque 4 (15:00 - 16:00)", opciones: ["15:00", "15:30"] },
    { id: "Bloque 5 (16:00 - 17:00)", opciones: ["16:00", "16:30"] },
    { id: "Bloque 6 (17:00 - 18:00)", opciones: ["17:00", "17:30"] }
];

const PublicBookingView = () => {
    const [step, setStep] = useState(1); 
    const [fecha, setFecha] = useState(null); 
    const [hora, setHora] = useState(null);
    const [horasOcupadas, setHorasOcupadas] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [form, setForm] = useState({ nombre: '', email: '', telefono: '', notas: '' });

    // 🔥 JARVIS: Restricción activada. Solo Martes (2) y Jueves (4)
    const esDiaHabil = (date) => {
        const day = date.getDay();
        return day === 2 || day === 4;
    };

    useEffect(() => {
        if (!fecha) return; 
        const cargarDisponibilidad = async () => {
            const startOfDay = new Date(fecha);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(fecha);
            endOfDay.setHours(23, 59, 59, 999);
            const q = query(collection(db, 'citas'), where('fechaInicio', '>=', startOfDay), where('fechaInicio', '<=', endOfDay));
            const snapshot = await getDocs(q);
            const ocupadas = snapshot.docs.map(doc => {
                const date = doc.data().fechaInicio.toDate();
                return date.getHours().toString().padStart(2, '0') + ":" + date.getMinutes().toString().padStart(2, '0');
            });
            setHorasOcupadas(ocupadas);
        };
        cargarDisponibilidad();
    }, [fecha]);

    const enviarConfirmacionEmail = async (datosCita) => {
        const templateParams = {
            nombreCliente: datosCita.nombre,
            emailCliente: datosCita.email,
            fecha: datosCita.fechaStr,
            hora: datosCita.hora,
            notas: datosCita.notas || "Sin notas",
            zoomLink: datosCita.zoomLink || "No requerido" // 👈 Pasamos el link al template de tu EmailJS
        };

        try {
            // 🔥 CORRECCIÓN CRÍTICA DE PUBLIC KEY (sfiGikjVk7t3NudEy)
            await emailjs.send(
                'service_ywv3c8t',    // Service ID OK
                'template_8to87xj',   // Template ID OK
                templateParams,
                'sfiGikjVk7t3NudEy'   // 👈 CLAVE CORREGIDA (Era una 'i', no una 'l')
            );
            console.log("GRID: Email enviado satisfactoriamente con enlace de Zoom");
        } catch (error) {
            console.error("Error al enviar el correo:", error);
            throw error; 
        }
    };

    const handleNext = () => {
        if (!fecha || !hora) return toast.error("Selecciona fecha y hora.");
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const [h, m] = hora.split(':');
            const fechaFinal = new Date(fecha);
            fechaFinal.setHours(parseInt(h), parseInt(m), 0);

            let zoomLink = '';
            let zoomStartLink = '';

            // ⚡ INTEGRACIÓN CON EL SERVIDOR PUENTE DE ZOOM
            try {
                const zoomResponse = await fetch('http://localhost:5001/api/zoom-meeting', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        topic: `Reunión Hoppy Time: ${form.nombre}`,
                        startTime: fechaFinal.toISOString(),
                        duration: 30
                    })
                });

                if (zoomResponse.ok) {
                    const zoomData = await zoomResponse.json();
                    zoomLink = zoomData.join_url || '';
                    zoomStartLink = zoomData.start_url || '';
                } else {
                    console.warn("GRID: El micro-servicio de Zoom falló o no está encendido, guardando cita sin link.");
                }
            } catch (zError) {
                console.error("GRID: No se pudo conectar con el micro-servicio server-zoom.js:", zError);
            }

            // Guardamos en Firestore incluyendo los enlaces generados de la videollamada
            await addDoc(collection(db, 'citas'), {
                nombreCliente: form.nombre,
                emailCliente: form.email,
                telefonoCliente: form.telefono,
                fechaInicio: Timestamp.fromDate(fechaFinal),
                notas: form.notas,
                origen: 'Web Pública',
                creadoEn: Timestamp.now(),
                estado: 'Confirmada',
                zoomLink: zoomLink,             // Link público para la sección interna y cliente
                zoomStartLink: zoomStartLink    // Link de administrador para que inicies la reunión
            });

            await enviarConfirmacionEmail({
                nombre: form.nombre,
                email: form.email,
                fechaStr: fecha.toLocaleDateString('es-ES'),
                hora: hora,
                notas: form.notas,
                zoomLink: zoomLink // Enviado al email
            });

            setStep(3); 
        } catch (error) {
            toast.error("El sistema de correo falló."); 
        }
        setLoading(false);
    };

    if (step === 3) {
        return (
            <div className="min-h-screen bg-teal-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full flex flex-col items-center">
                    <img src={miLogo} alt="Logo" className="w-24 h-24 mb-4" />
                    <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-4"><FaCheckCircle size={30} /></div>
                    <h2 className="text-2xl font-bold mb-2">¡Reserva Confirmada!</h2>
                    <p className="text-gray-600 mb-6">Gracias <strong>{form.nombre}</strong>. Revisa tu correo: <strong>{form.email}</strong></p>
                    <button onClick={() => window.location.reload()} className="text-teal-600 font-bold hover:underline uppercase">Volver al inicio</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">
            <Toaster position="top-center" />
            <div className="mb-8 text-center flex flex-col items-center">
                <img src={miLogo} alt="Logo" className="w-28 h-28 mb-4 hover:scale-105 transition-transform" />
                <h1 className="text-3xl font-extrabold text-gray-800 uppercase tracking-tighter">Hoppy Time</h1>
                <p className="text-gray-500 font-bold uppercase text-xs tracking-widest mt-2">Sincronización en el GRID</p>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row min-h-[500px]">
                <div className={`p-8 bg-teal-600 text-white md:w-1/3 flex flex-col ${step === 2 ? 'hidden md:flex' : ''}`}>
                    <h3 className="text-sm font-black mb-6 uppercase tracking-widest flex items-center gap-2"><FaCalendarAlt /> 1. Fecha</h3>
                    <div className="custom-datepicker-wrapper flex-grow flex justify-center text-black">
                        <DatePicker selected={fecha} onChange={(date) => setFecha(date)} inline locale="es" minDate={new Date()} filterDate={esDiaHabil} />
                    </div>
                </div>

                <div className="p-10 md:w-2/3 flex flex-col bg-white">
                    {step === 1 && (
                        <>
                            <h3 className="text-sm font-black text-gray-400 mb-8 uppercase tracking-widest flex items-center gap-2"><FaClock className="text-teal-500" /> 2. Horarios</h3>
                            {!fecha ? (
                                <p className="text-gray-400 text-center py-10 italic">👈 Selecciona un día válido.</p>
                            ) : (
                                <div className="space-y-6">
                                    {BLOQUES_HORARIOS.map((bloque) => {
                                        return (
                                            <div key={bloque.id} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase mb-3 px-2">{bloque.id}</p>
                                                {/* 🔥 JARVIS: Restaurado el grid de 2 columnas para mantener la estética de pares */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    {bloque.opciones.map(h => {
                                                        const isTaken = horasOcupadas.includes(h);
                                                        return (
                                                            <button key={h} disabled={isTaken} onClick={() => setHora(h)}
                                                                className={`py-3 px-4 rounded-xl text-sm font-black transition-all ${isTaken ? 'bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed' : hora === h ? 'bg-teal-600 text-white border-teal-600 shadow-md' : 'bg-white border-gray-200 text-gray-600'}`}
                                                            >{h}</button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="mt-auto pt-8 flex justify-end">
                                <button onClick={handleNext} disabled={!hora} className="bg-slate-800 text-white px-10 py-3 rounded-xl font-bold shadow-lg">Continuar &rarr;</button>
                            </div>
                        </>
                    )}
                    {step === 2 && (
                        <form onSubmit={handleSubmit} className="flex flex-col h-full">
                            <button type="button" onClick={() => setStep(1)} className="text-gray-400 mb-4 flex items-center gap-1 text-xs font-bold uppercase tracking-widest"><FaArrowLeft /> Volver</button>
                            <h3 className="text-xl font-black text-gray-800 mb-6 uppercase tracking-tighter">Confirmar para las {hora} hs</h3>
                            <div className="space-y-4 flex-grow">
                                <input required placeholder="Nombre Completo" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-medium" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
                                <input required type="email" placeholder="Tu Email" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-medium" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                                <input required placeholder="Teléfono" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-medium" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
                                <textarea placeholder="Notas adicionales" className="w-full p-3 border rounded-xl h-24 focus:ring-2 focus:ring-teal-500 outline-none resize-none font-medium" value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}></textarea>
                            </div>
                            <button type="submit" disabled={loading} className="mt-8 w-full bg-teal-600 text-white py-4 rounded-xl font-bold shadow-lg uppercase tracking-widest">
                                {loading ? 'Sincronizando...' : 'Confirmar Reserva'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicBookingView;